import dayjs from "dayjs";
import { Edit, useForm, useSelect } from "@refinedev/antd";
import { Form, Input, Select, Tag, DatePicker, InputNumber, Space, Row, Col } from "antd";
import { IResourceComponentsProps } from "@refinedev/core";
import {
  FileTextOutlined,
  CalendarOutlined,
  SettingOutlined,
  RobotOutlined,
} from "@ant-design/icons";

import { ILicence, ISubscription } from "interfaces";
import React from "react";
import { FormSection, FormFieldGrid } from "components/form";

export const SubscriptionEdit: React.FC<IResourceComponentsProps> = () => {
  const {
    formProps,
    saveButtonProps,
    query,
    formLoading: isLoading,
  } = useForm<ISubscription>({
    action: "edit",
  });

  function LicenceSelectComponent() {
    const { selectProps } = useSelect<ILicence>({
      resource: "licence",
      optionLabel: "ukhoLicenceId",
      optionValue: "id",

      filters: query?.data?.data?.licence?.id
        ? [
            {
              field: "id",
              operator: "eq",
              value: query?.data?.data?.licence?.id,
            },
          ]
        : [],

      pagination: {
        mode: "server"
      }
    });
    return (
      <Form.Item
        label="UKHO Licence"
        name={["licence", "id"]}
        rules={[{ required: true }]}
        initialValue={query?.data?.data?.licence?.ukhoLicenceId}
      >
        <Select {...selectProps} disabled />
      </Form.Item>
    );
  }

  return query?.isLoading ? (
    <React.Fragment>'Loading...'</React.Fragment>
  ) : (
    <Edit
      breadcrumb={false}
      saveButtonProps={saveButtonProps}
      headerProps={{
        onBack: () => window.history.back(),
        subTitle: "edit subscription",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
    >
      <Form {...formProps} layout="vertical">
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <FormSection
            title="Subscription Details"
            subtitle="Basic subscription information"
            icon={<FileTextOutlined />}
            variant="elevated"
          >
            <FormFieldGrid columns={2}>
              <Form.Item label="Subscription Name" name="subscriptionName">
                <Input />
              </Form.Item>
              <Form.Item label="Service Type" name="serviceType">
                <Input />
              </Form.Item>
            </FormFieldGrid>
            <Row gutter={[24, 16]}>
              <Col xs={24} md={12}>
                {query?.isSuccess ? <LicenceSelectComponent /> : null}
              </Col>
            </Row>
          </FormSection>

          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <FormSection
                title="Dates"
                subtitle="Important dates"
                icon={<CalendarOutlined />}
              >
                <Form.Item
                  label="Enrollment Date"
                  name="enrollmentDate"
                  rules={[{ required: true }]}
                  getValueProps={(value) => ({
                    value: value ? dayjs(value) : "",
                  })}
                >
                  <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                </Form.Item>
              </FormSection>
            </Col>

            <Col xs={24} lg={12}>
              <FormSection
                title="Status Settings"
                subtitle="Subscription status configuration"
                icon={<SettingOutlined />}
              >
                <FormFieldGrid columns={2}>
                  <Form.Item
                    label="Licence Type"
                    name="licenceType"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={[
                        { label: "Full", value: "full" },
                        { label: "Trial", value: "trial" },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Activation Status"
                    name="activationStatus"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={[
                        { label: "Active", value: "active" },
                        { label: "Inactive", value: "inactive" },
                      ]}
                    />
                  </Form.Item>
                </FormFieldGrid>
                <FormFieldGrid columns={2}>
                  <Form.Item
                    label="Hold Status"
                    name="holdStatus"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={[
                        { label: "Not On Hold", value: "not on hold" },
                        { label: "On Hold", value: "on hold" },
                      ]}
                    />
                  </Form.Item>
                </FormFieldGrid>
              </FormSection>
            </Col>
          </Row>

          <FormSection
            title="Automation Settings"
            subtitle="Order and permit automation rules"
            icon={<RobotOutlined />}
            variant="subtle"
          >
            <FormFieldGrid columns={3}>
              <Form.Item
                label="Order Value Threshold ($)"
                name="orderValueThreshold"
                rules={[{ required: true }]}
              >
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                label="Automated Ordering"
                name="automatedOrdering"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { label: "Permitted", value: "permitted" },
                    { label: "Not Permitted", value: "not permitted" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Automated Permit Issuance"
                name="automatedPermitIssuance"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { label: "Permitted", value: "permitted" },
                    { label: "Not Permitted", value: "not permitted" },
                  ]}
                />
              </Form.Item>
            </FormFieldGrid>
          </FormSection>
        </Space>
      </Form>
    </Edit>
  );
};
